import { useEffect, useRef } from 'react'

/**
 * Polls `callback` every `intervalMs` milliseconds while `enabled` is true.
 * Pauses automatically when the document tab is hidden (document.hidden).
 * Resumes when the tab becomes visible again.
 * The interval timer is always cleared on unmount or when `enabled` becomes false.
 */
export function usePolling(callback: () => void, intervalMs: number, enabled = true) {
  const savedCallback = useRef(callback)

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (!enabled) return

    let id: ReturnType<typeof setInterval> | null = null

    function start() {
      if (id != null) return
      id = setInterval(() => {
        if (!document.hidden) {
          savedCallback.current()
        }
      }, intervalMs)
    }

    function stop() {
      if (id != null) {
        clearInterval(id)
        id = null
      }
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        stop()
      } else {
        // Resume: fire immediately then restart interval
        savedCallback.current()
        start()
      }
    }

    if (!document.hidden) {
      start()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      stop()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [intervalMs, enabled])
}
